/**
 * HTTP interceptors for production local auth (Phase 5.8), installed once at
 * startup. They are transparent no-ops in development mode (no auth cookies).
 *
 *   1. CSRF: on same-origin unsafe requests to /api/*, attach the `x-csrf-token`
 *      header read from the readable `yzc_csrf` cookie (double-submit).
 *   2. Auto-refresh: when an /api/v1 request (other than the auth endpoints)
 *      returns 401, transparently POST /auth/refresh once and retry the original
 *      request. If refresh fails, broadcast `auth:session-expired` so the app can
 *      redirect to the login / session-expired screen.
 *
 * Bodies are strings in this codebase, so retrying with the same init is safe.
 */

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);
export const SESSION_EXPIRED_EVENT = "auth:session-expired";

function readCookie(name: string): string | undefined {
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]!) : undefined;
}

function isApiUrl(url: string): boolean {
  return url.startsWith("/api/");
}
function isAuthEndpoint(url: string): boolean {
  return url.startsWith("/api/v1/auth/");
}

let installed = false;
let refreshInFlight: Promise<boolean> | null = null;

/** Attempt a single token refresh, deduped across concurrent 401s. */
function refreshOnce(originalFetch: typeof fetch): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = originalFetch("/api/v1/auth/refresh", {
      method: "POST",
      headers: { "x-csrf-token": readCookie("yzc_csrf") ?? "" },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

export function installHttpInterceptors(): void {
  if (installed) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (
    input: RequestInfo | URL,
    init: RequestInit = {},
  ): Promise<Response> => {
    const url = typeof input === "string" ? input : input.toString();
    const method = (init.method ?? "GET").toUpperCase();

    // 1. CSRF header on unsafe same-origin API requests.
    if (isApiUrl(url) && UNSAFE.has(method)) {
      const csrf = readCookie("yzc_csrf");
      if (csrf) {
        const headers = new Headers(init.headers);
        if (!headers.has("x-csrf-token")) headers.set("x-csrf-token", csrf);
        init = { ...init, headers };
      }
    }

    const res = await originalFetch(input, init);

    // 2. Transparent refresh + retry on 401 (never for the auth endpoints).
    if (res.status === 401 && isApiUrl(url) && !isAuthEndpoint(url)) {
      const refreshed = await refreshOnce(originalFetch);
      if (refreshed) {
        // Re-attach a possibly-rotated CSRF token, then retry once.
        if (UNSAFE.has(method)) {
          const csrf = readCookie("yzc_csrf");
          if (csrf) {
            const headers = new Headers(init.headers);
            headers.set("x-csrf-token", csrf);
            init = { ...init, headers };
          }
        }
        return originalFetch(input, init);
      }
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }

    return res;
  };
}
